// "How this is calculated" — read-only explainer for the TDEE shown in
// SummaryHero's TdeeCard. Opened from that card's own text-button; there is
// nothing to save, so — same as TdeeCalculatorModal.js, the other read-only
// modal in this feature — there's no footer at all, just the Modal's own
// close control.
//
// Always reachable whenever TdeeCard renders (same gate as its "Calculate"
// button) — including when the number on the card is a manual override with
// no back-calc behind it at all. The two cases genuinely need different
// content, so the modal branches on `expenditure.confident`:
//   - confident: the first section reproduces THIS client's own inputs and
//     the arithmetic that produced their number, so a coach can audit the
//     figure rather than take it on faith.
//   - !confident: there's no calculation to reproduce (the visible number was
//     typed in via Adjust, possibly copied from the separate Calculate
//     modal — the two aren't wired together, so this component has no way to
//     tell) — so the first section explains why there's no automatic number
//     yet, reusing NeedMoreData's own missing-requirements copy.
// The method/accuracy sections after that are shared, and the conditional
// callouts at the end surface the same flags the card itself reacts to
// (decoupled, staleData, a coach override).

import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import Modal from "../../../../../parts/modal-new";
import { toDisplayWeight } from "./viewTokens";

// hero.* (not hero.explainerModal.*) — reuses NeedMoreData's own keys for
// the "what's missing" list, so the two places describing the same 3
// thresholds can't drift apart into 2 separately-worded copies.
const h = (t, key, opts) => t(`nutritionPage.progress.hero.${key}`, opts);
const te = (t, key, opts) => t(`nutritionPage.progress.hero.explainerModal.${key}`, opts);

// Energy the weight trend accounts for, in kcal/day. Derived rather than read
// off `expenditure` because buildEnergyExpenditureStats keeps balancePerDay
// local — and deriving it from estExpenditure (not workingExpenditure) keeps
// this section describing the CALCULATION even when a coach override is
// active, which the override callout below then explains separately.
const balancePerDay = (expenditure) =>
  Math.abs(expenditure.avgIntake - expenditure.estExpenditure);

// Which of the 3 directional sentences applies. Anything under 0.05 kg/week
// rounds to "0.0" in the table above, so calling it a trend would contradict
// what the coach is reading.
const trendDirection = (weeklyChange) => {
  if (weeklyChange <= -0.05) return "losing";
  if (weeklyChange >= 0.05) return "gaining";
  return "stable";
};

// The "why there's no number yet" list — same 3 thresholds NeedMoreData
// already renders, same conditional (only the ones still unmet), so a coach
// who's seen that card recognizes this instantly instead of parsing new copy.
const MissingDataList = ({ t, expenditure }) => (
  <ul className="np-tdee-explainer__list">
    {expenditure.totalDays < expenditure.needs.minDays && (
      <li>{h(t, "daysInRange", { total: expenditure.totalDays, min: expenditure.needs.minDays })}</li>
    )}
    {expenditure.loggedDays < expenditure.needs.minLogged && (
      <li>{h(t, "daysLogged", { logged: expenditure.loggedDays, min: expenditure.needs.minLogged })}</li>
    )}
    {expenditure.weighInCount < expenditure.needs.minWeighIns && (
      <li>{h(t, "weighIns", { count: expenditure.weighInCount, min: expenditure.needs.minWeighIns })}</li>
    )}
  </ul>
);

MissingDataList.propTypes = {
  t: PropTypes.func.isRequired,
  expenditure: PropTypes.object.isRequired,
};

export const TdeeExplainerModal = ({ isOpen, onClose, expenditure, units }) => {
  const { t } = useTranslation("athlete-portal");
  if (!isOpen) return null;

  const isImperial = units === "imperial";
  const weightUnit = isImperial ? "lb" : "kg";
  const isConfident = expenditure.confident;

  // Only the confident branch of buildEnergyExpenditureStats populates
  // avgIntake/estExpenditure/weeklyChange — computing these unconditionally
  // would crash on a manual-only override (no back-calc ever ran).
  const direction = isConfident ? trendDirection(expenditure.weeklyChange) : null;
  const numbers = isConfident
    ? {
        avgIntake: expenditure.avgIntake.toLocaleString(),
        estExpenditure: expenditure.estExpenditure.toLocaleString(),
        balance: balancePerDay(expenditure).toLocaleString(),
        rate: Math.abs(toDisplayWeight(expenditure.weeklyChange, isImperial)).toFixed(1),
        unit: weightUnit,
      }
    : null;

  return (
    <Modal
      title={te(t, "title")}
      className="np-tdee-modal np-tdee-explainer"
      onClose={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <div className="body np-tdee-modal__body np-tdee-explainer__body">
        {isConfident ? (
          /* --- this client's own numbers --- */
          <section className="np-tdee-explainer__section">
            <h4 className="np-tdee-explainer__heading">
              {te(t, "resultHeading", { value: numbers.estExpenditure })}
            </h4>
            <dl className="np-tdee-explainer__figures">
              <div className="np-tdee-explainer__figure">
                <dt>{te(t, "figureConsumed")}</dt>
                <dd>{te(t, "kcalDay", { value: numbers.avgIntake })}</dd>
              </div>
              <div className="np-tdee-explainer__figure">
                <dt>{te(t, "figureTrend")}</dt>
                <dd>
                  {te(t, `trendValue.${direction}`, { value: numbers.rate, unit: weightUnit })}
                </dd>
              </div>
              <div className="np-tdee-explainer__figure np-tdee-explainer__figure--result">
                <dt>{te(t, "figureExpenditure")}</dt>
                <dd>{te(t, "kcalDay", { value: numbers.estExpenditure })}</dd>
              </div>
            </dl>
            <p className="np-tdee-explainer__meta">
              {te(t, "basedOn", {
                logged: expenditure.loggedDays,
                weighIns: expenditure.weighInCount,
                days: expenditure.totalDays,
              })}
            </p>
            <p className="np-tdee-explainer__body-text">
              {te(t, `inference.${direction}`, numbers)}
            </p>
          </section>
        ) : (
          /* --- why there's no calculated number yet --- */
          <section className="np-tdee-explainer__section">
            <h4 className="np-tdee-explainer__heading">{te(t, "notEnoughDataHeading")}</h4>
            <p className="np-tdee-explainer__body-text">{te(t, "notEnoughDataBody")}</p>
            <MissingDataList t={t} expenditure={expenditure} />
          </section>
        )}

        {/* --- how the method works (always shown — describes the automatic
            method itself, not necessarily how THIS number was produced) --- */}
        <section className="np-tdee-explainer__section">
          <h4 className="np-tdee-explainer__heading">{te(t, "methodHeading")}</h4>
          <p className="np-tdee-explainer__body-text">{te(t, "methodIntro")}</p>
          <p className="np-tdee-explainer__body-text">{te(t, "methodEnergy")}</p>
          <p className="np-tdee-explainer__body-text">{te(t, "methodRegression")}</p>
        </section>

        {/* --- limits of the estimate — only meaningful once there IS an
            estimate; the !confident state already covers its own minimums
            via MissingDataList above, so this would just repeat them. --- */}
        {isConfident && (
          <section className="np-tdee-explainer__section">
            <h4 className="np-tdee-explainer__heading">{te(t, "accuracyHeading")}</h4>
            <p className="np-tdee-explainer__body-text">{te(t, "accuracyIntro")}</p>
            <ul className="np-tdee-explainer__list">
              {["logging", "weighIns", "water"].map((key) => (
                <li key={key}>
                  <strong>{te(t, `accuracyItems.${key}.label`)}</strong>{" "}
                  {te(t, `accuracyItems.${key}.text`)}
                </li>
              ))}
            </ul>
            <p className="np-tdee-explainer__meta">{te(t, "accuracyMinimums")}</p>
          </section>
        )}

        {/* --- conditional callouts: the same flags the card itself reacts to.
            decoupled/staleData only ever come back truthy on the confident
            branch, so these are naturally inert in the !confident case. --- */}
        {expenditure.decoupled && (
          <p className="np-tdee-explainer__callout np-tdee-explainer__callout--warn">
            {te(t, "calloutDecoupled", { value: numbers.rate, unit: weightUnit })}
          </p>
        )}
        {expenditure.staleData && (
          <p className="np-tdee-explainer__callout np-tdee-explainer__callout--warn">
            {te(t, "calloutStale")}
          </p>
        )}
        {expenditure.hasOverride && (
          <p className="np-tdee-explainer__callout">
            {expenditure.overrideMode === "manual"
              ? te(t, "calloutOverrideManual", {
                  value: expenditure.workingExpenditure.toLocaleString(),
                })
              : te(t, "calloutOverrideCalc", {
                  pct: expenditure.underLogPct,
                  value: expenditure.workingExpenditure.toLocaleString(),
                })}
          </p>
        )}
      </div>
    </Modal>
  );
};

TdeeExplainerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  expenditure: PropTypes.object.isRequired,
  units: PropTypes.string,
};

export default TdeeExplainerModal;
